import axios from 'axios';
import qs from 'qs';
import { SHOPPING_CART, WISH_LIST } from 'redux/domain/Cart/constants';
import CartItem from 'models/CartItem';
import config from '../apiConfig';
import { getToken } from '../Helpers';
import { AUTH_TOKEN } from '../Helpers/constants';
import { modes } from 'react-transition-group/SwitchTransition';

/**
 * Validate that required arguments were passed and passed arguments
 * are of the correct type.
 * @param {object} args object literal of arguments
 */
const validateArgs = args => {
	// React Router? casts numbers as strings. Our endpoint doesn't care if a
	// number comes in as a string or integer but checking typeof 'number'
	// does not work so we try to cast to an int.
	Object.keys(args).forEach(argName => {
		const val = args[argName];
		switch (argName) {
			case 'cartId':
				if (val == null) {
					throw new Error('Cart id is required.');
				}
				if (Number.isNaN(parseInt(val, 10))) {
					throw new TypeError('Invalid cart id.');
				}
				break;

			case 'cartItem':
				if (val == null) {
					throw new Error('Cart item is required.');
				}
				if (!(val instanceof CartItem)) {
					throw new TypeError('Invalid cart item.');
				}
				break;
			case 'cartType':
				if (val == null) {
					throw new Error('Cart type is required.');
				}
				if (val !== SHOPPING_CART && val !== WISH_LIST) {
					throw new TypeError('Invalid cart type.');
				}
				break;
			case 'customerId':
				if (val == null) {
					throw new Error('Customer id is required.');
				}
				if (Number.isNaN(parseInt(val, 10))) {
					throw new TypeError('Invalid customer id.');
				}
				break;
			case 'productId':
				if (val == null) {
					throw new Error('Product id is required.');
				}
				if (Number.isNaN(parseInt(val, 10))) {
					throw new TypeError('Invalid product id.');
				}
				break;
			case 'sync':
				if (typeof val !== 'boolean') {
					throw new TypeError('Invalid sync request type.');
				}
				break;
			default:
				break;
		}
	});
};

/**
 * Returns a promise for cart items for a specified customer and cart type
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId integer representing the customer id
 * @param {number} cartType integer
 * 			value: 1 for shopping cart or
 *             2 for wishlist
 * @param {boolean} requestSync request a STORIS -> eSTORIS sync before retrieving the cart
 * 			This option is mutually exclusive with Wishlists since wishlists cannot be synced.
 *      This can be time consuming so should only be specified on a first fetch.
 * @returns promise
 * @throws TypeError/Error
 */
const getCartItems = (cancelToken, customerId, cartType, requestSync = false) => {
	let sync = requestSync;
	const args = {
		customerId,
		cartType,
		sync,
	};

	validateArgs(args);

	if (sync && cartType === WISH_LIST) {
		sync = false;
	}

	const instance = axios.create(config);
	return instance.get('ShoppingCartItems', {
		headers: {
			Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
		},
		params: {
			customerId,
			cartType,
			sync,
		},
		cancelToken,
	});
};

/**
 * Adds an item to the cart. Returns an array called errors.
 * If there were no errors, this will be empty.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId
 * @param {number} productId
 * @param {number} cartType 1: shopping cart / 2: wish list
 * @param  {CartItem} cartItem
 * quantity - integer (optional): defaults to 1,
 * storeId - integer (optional): default store is used,
 * customerEnteredPrice - decimal (optional): defaults to 0,
 * rentalStartDate - DateTime (optional): defaults to null,
 * rentalEndDate - DateTime (optional): defaults to null,
 * productAttributeValues - array of ints (optional): kit component/addons
 * @returns promise
 */
const addCartItem = (cancelToken, customerId, productId, selectedStoreId, cartType, cartItem) => {
	const args = {
		customerId,
		productId,
		cartType,
		selectedStoreId,
		cartItem,
	};

	validateArgs(args);

	const instance = axios.create(config);

	return instance.post(
		'ShoppingCartItem/Add',
		qs.stringify({
			customerId,
			productId,
			cartType,
			quantity: cartItem.quantity,
			storeId: cartItem.storeId,
			warehouseId: selectedStoreId,
			customerEnteredPrice: cartItem.customerEnteredPrice,
			rentalStartDate: cartItem.rentalStartDate,
			rentalEndDate: cartItem.rentalEndDate,
			productAttributeValues: cartItem.productAttributeValues,
			productFamilyMemberAttributeValues: cartItem.productFamilyMemberAttributeValues,
			selectedProductFamilyMemberProductId: cartItem.selectedProductFamilyMemberProductId,
		}),
		{
			headers: {
				Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
			},
			cancelToken,
		},
	);
};

/**
 * Deletes all items from a shopping cart or wish list.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId - integer (required),
 * @param {number} cartType - integer (required) 1: shopping cart / 2: wishlist
 * @returns promise
 * @throws Error
 */
const deleteCart = (cancelToken, customerId, cartType) => {
	const args = {
		customerId,
		cartType,
	};

	validateArgs(args);

	const instance = axios.create(config);

	return instance.post(
		'ShoppingCart/Delete',
		qs.stringify({
			customerId,
			cartType,
		}),
		{
			headers: {
				Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
			},
			cancelToken,
		},
	);
};

/**
 * Deletes an item from the cart. Returns an array called errors.
 * If there were no errors, this will be empty.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId - integer (required),
 * @param {number} cartId - integer (required),
 * @param {number} cartType - integer (required) 1: shopping cart / 2: wishlist
 * 	not needed to delete an item but required to trigger a refetch.
 * @returns promise
 * @throws Error
 */
const deleteCartItem = (cancelToken, customerId, cartId, cartType) => {
	const args = {
		customerId,
		cartId,
		cartType,
	};

	validateArgs(args);

	const instance = axios.create(config);

	return instance.post(
		'ShoppingCartItem/Delete',
		qs.stringify({
			customerId,
			cartId,
		}),
		{
			headers: {
				Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
			},
			cancelToken,
		},
	);
};

/**
 * Updates an item to the cart. Returns an array called errors.
 * If there were no errors, this will be empty.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId
 * @param {CartItem} cartItem
 * cartId - integer (required),
 * quantity - integer (required, NOTE: a quantity of zero deletes the cart item,
 * attributes - string (optional): product attributes in xml format,
 * customerEnteredPrice - decimal (optional): defaults to 0,
 * rentalStartDate - DateTime (optional): defaults to null,
 * rentalEndDate - DateTime (optional): defaults to null,
 * @returns promise
 */
const updateCartItem = (cancelToken, customerId, cartItem) => {
	const args = {
		customerId,
		cartItem,
	};

	validateArgs(args);

	const instance = axios.create(config);

	return instance.post(
		'ShoppingCartItem/Update',
		qs.stringify({
			customerId,
			cartId: cartItem.cartId,
			quantity: cartItem.quantity,
			customerEnteredPrice: cartItem.customerEnteredPrice,
			rentalStartDate: cartItem.rentalStartDate,
			rentalEndDate: cartItem.rentalEndDate,
		}),
		{
			headers: {
				Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
			},
			cancelToken,
		},
	);
};

/**
 * Email shopping cart. Returns an array called errors.
 * If there were no errors, this will be empty.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress
 * @param {number} customerId
 * @param {string} alternateEmail
 * @param {string} customerEmail
 * @param {string} customerName
 * @returns promise
 */
const emailCart = (cancelToken, customerId, alternateEmail, customerEmail, customerName) => {
	const args = {
		customerId,
		alternateEmail,
		customerEmail,
		customerName
	};

	validateArgs(args);

	const instance = axios.create(config);

	return instance.post(
		'ShoppingCartItem/Email',
		qs.stringify({
			customerId,
			alternateEmail,
			customerEmail,
			customerName
		}),
		{
			headers: {
				Authorization: `Bearer ${getToken(AUTH_TOKEN)}`,
			},
			cancelToken,
		},
	);
};

export {
	getCartItems,
	addCartItem,
	deleteCart,
	deleteCartItem,
	updateCartItem,
	validateArgs,
	emailCart,
};
